import React from "react";
import { Link } from "react-router-dom";
import { CenteredCell } from "../../components/styled";
import {
  formatDateString,
  formatCurrency,
  formatNumber,
  formatPercent,
} from "library/utils";

import { ExpandedColumn } from "library/types/columns";
import { ContextButton } from "./components/ContextButton";
import { StatusCell } from "./components/StatusCell";
import { UsageTypeCell } from "./components/UsageTypeCell";
import { PowerTypeCell } from "./components/PowerTypeCell";
import { TransportTypeCell } from "./components/TransportTypeCell";
import { VehicleTypeCell } from "./components/VehicleTypeCell";
import {
  BriefcaseRegular,
  PersonRegular,
  VehicleCarRegular,
  ShieldRegular,
} from "@fluentui/react-icons";
import styled from "styled-components";

const RightAlignedCell = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end !important;
`;

const InsurerIcon = styled.img`
  vertical-align: middle;
  max-height: 20px;
  margin-left: 5px;
  margin-right: 5px;
  cursor: pointer;
`;

const InsurerPlaceholder = styled.div`
  display: inline-block;
  width: 33px;
  height: 20px;
  margin-left: 5px;
  margin-right: 5px;
  background-color: transparent;
`;

// Fixed order of insurers for consistent display
const INSURERS_ORDER = [
  "Balcia",
  "Balta",
  "BAN",
  "BTA",
  "Compensa",
  "Ergo",
  "IF",
];

/**
 * The ten columns of an invoice payer (all hidden by default): <prefix>IsResident, <prefix>IsCompany, <prefix>Name,
 * <prefix>Code, <prefix>FirstName, <prefix>LastName, <prefix>AddressAddressString / City / Email / Phone.
 * Column label keys are <labelPrefix><Field>.label, e.g. qtg.invoiceMtplPayerName.label.
 */
const invoicePayerColumns = (
  prefix: "InvoiceMtplPayer" | "InvoiceCascoPayer" | "GroupInvoicePayer",
  labelPrefix: string,
): ExpandedColumn[] => {
  const textColumn = (
    field: string,
    width: number,
    withFilter = true,
  ): ExpandedColumn => ({
    key: `${prefix}${field}`,
    labelKey: `${labelPrefix}${field}.label`,
    width,
    isVisible: false,
    ...(withFilter ? { filter: { type: "text" as const, value: "" } } : {}),
    detailsListColumn: ({ t, width }) => ({
      key: `${prefix}${field}`,
      label: t(`${labelPrefix}${field}.label`),
      width,
      isSortable: true,
    }),
  });
  return [
    {
      key: `${prefix}IsResident`,
      labelKey: `${labelPrefix}IsResident.label`,
      width: 120,
      isVisible: false,
      filter: {
        type: "boolean",
        value: null,
      },
      detailsListColumn: ({ t, width }) => ({
        key: `${prefix}IsResident`,
        label: t(`${labelPrefix}IsResident.label`),
        width,
        isSortable: true,
        render: ({ row }) => (
          <CenteredCell>
            {row[`${prefix}IsResident`] != null
              ? row[`${prefix}IsResident`]
                ? t("greco.yes")
                : t("greco.no")
              : null}
          </CenteredCell>
        ),
      }),
    },
    {
      key: `${prefix}IsCompany`,
      labelKey: `${labelPrefix}IsCompany.label`,
      width: 100,
      isVisible: false,
      filter: {
        type: "boolean",
        value: null,
      },
      detailsListColumn: ({ t, width }) => ({
        key: `${prefix}IsCompany`,
        label: t(`${labelPrefix}IsCompany.label`),
        width,
        isSortable: true,
        render: ({ row }) => {
          if (row[`${prefix}IsCompany`] == null) return null;
          return (
            <CenteredCell>
              {row[`${prefix}IsCompany`] ? (
                <BriefcaseRegular
                  fontSize={20}
                  title={t("qtg.payerType.company.label")}
                />
              ) : (
                <PersonRegular
                  fontSize={20}
                  title={t("qtg.payerType.person.label")}
                />
              )}
            </CenteredCell>
          );
        },
      }),
    },
    textColumn("Name", 220),
    textColumn("Code", 120),
    textColumn("FirstName", 120),
    textColumn("LastName", 120),
    textColumn("AddressAddressString", 200, false),
    textColumn("AddressCity", 120),
    textColumn("AddressEmail", 150),
    textColumn("AddressPhone", 150),
  ];
};

export const defaultCols: ExpandedColumn[] = [
  // V OfferId
  {
    key: "OfferId",
    labelKey: "qtg.offerId.label",
    width: 80,
    isVisible: true,
    isFixed: true,
    detailsListColumn: ({ t, width }) => ({
      key: "OfferId",
      label: t("qtg.offerId.label"),
      width,
      isSortable: true,
      render: ({ row }) => {
        if (!row?.OfferId) return null;

        // Determine product type
        const hasCasco =
          row?.OfferedInsurersCasco && row.OfferedInsurersCasco.length > 0;
        const hasMtpl =
          row?.OfferedInsurersMtpl && row.OfferedInsurersMtpl.length > 0;

        let productTitle = "";
        let showCasco = false;
        let showMtpl = false;

        if (hasCasco && hasMtpl) {
          productTitle = "CASCO + MTPL";
          showCasco = true;
          showMtpl = true;
        } else if (hasCasco) {
          productTitle = "CASCO";
          showCasco = true;
        } else if (hasMtpl) {
          productTitle = "MTPL";
          showMtpl = true;
        }

        return (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            {(showCasco || showMtpl) && (
              <div
                data-help="offers-product-icons"
                data-help-product={productTitle}
                title={productTitle}
                style={{ display: "flex", alignItems: "center", gap: "2px" }}
              >
                {showCasco && (
                  <VehicleCarRegular
                    style={{ fontSize: "14px", color: "#0078d4" }}
                  />
                )}
                {showMtpl && (
                  <ShieldRegular
                    style={{ fontSize: "14px", color: "#d83b01" }}
                  />
                )}
              </div>
            )}
            <Link
              data-help="offers-offer-id-link"
              to={`/offer/${row.OfferId}`}
              style={{
                color: "inherit",
                textDecoration: "none",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.textDecoration = "underline";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.textDecoration = "none";
              }}
            >
              {row.OfferId}
            </Link>
          </div>
        );
      },
    }),
  },

  {
    key: "options",
    labelKey: "",
    width: 40,
    isVisible: true,
    isFixed: true,
    detailsListColumn: ({ width }) => ({
      key: "options",
      label: "",
      isSortable: false,
      width,
      render: ({ row }) => {
        return (
          <CenteredCell>
            <ContextButton offer={row} />
          </CenteredCell>
        );
      },
    }),
  },
  // VF OfferStatus
  {
    key: "OfferStatus",
    labelKey: "qtg.status.label",
    width: 80,
    isVisible: true,
    isFixed: true,
    filter: {
      type: "taxonomy",
      taxonomyKey: "OfferStatus",
      value: [],
      exclude: ["OfferStatus.INVALIDATED"],
    },
    detailsListColumn: ({ t, width }) => ({
      key: "OfferStatus",
      label: t("qtg.status.label"),
      width,
      isSortable: true,
      render: ({ row }) => {
        if (!row) return null;
        return <StatusCell offerStatus={row.OfferStatus} />;
      },
    }),
  },
  // VF OfferedInsurers - combined MTPL and CASCO insurers with icons
  {
    key: "OfferedInsurers",
    labelKey: "qtg.insurers.label",
    width: 300,
    isVisible: true,
    isFixed: false,
    detailsListColumn: ({ t, width }) => ({
      key: "OfferedInsurers",
      label: t("qtg.insurers.label"),
      width,
      isSortable: false,
      render: ({ row }) => {
        if (!row) return null;

        // Combine MTPL and CASCO insurers
        const mtplInsurers = row.OfferedInsurersMtpl || [];
        const cascoInsurers = row.OfferedInsurersCasco || [];

        const mtplInsurer = row.InvoiceMtplInsurer;
        const cascoInsurer = row.InvoiceCascoInsurer;

        // only one of the two exists
        const insurer = mtplInsurer || cascoInsurer;

        // Create set of offered insurers (case-insensitive)
        const offeredInsurersSet = new Set(
          [...mtplInsurers, ...cascoInsurers].map((name) => name.toLowerCase()),
        );

        // Display insurers in fixed order with placeholders for missing ones
        const insurerIcons = INSURERS_ORDER.map((insurerName: string) => {
          const isOffered = offeredInsurersSet.has(insurerName.toLowerCase());

          if (isOffered) {
            return (
              <InsurerIcon
                key={insurerName}
                src={`/insurers/${insurerName.toLowerCase()}.png`}
                alt={insurerName}
                title={insurerName}
                style={
                  insurerName === insurer
                    ? {
                        filter:
                          "drop-shadow(0 0 4px rgba(255, 201, 131, 0.6))   /* tight halo */  drop-shadow(0 0 4px rgba(251, 255, 0, 1)) /* larger glow */",
                      }
                    : undefined
                }
              />
            );
          } else {
            return <InsurerPlaceholder key={insurerName} />;
          }
        });

        return <CenteredCell>{insurerIcons}</CenteredCell>;
      },
    }),
  },
  // F ClientIsResident
  {
    key: "ClientIsResident",
    labelKey: "qtg.clientIsResident.label",
    width: 120,
    isVisible: false,
    filter: {
      type: "boolean",
      value: null,
    },
    detailsListColumn: ({ t, width }) => ({
      key: "ClientIsResident",
      label: t("qtg.clientIsResident.label"),
      width,
      isSortable: true,
      render: ({ row }) => (
        <CenteredCell>
          {row.ClientIsResident ? t("greco.yes") : t("greco.no")}
        </CenteredCell>
      ),
    }),
  },
  // F ClientIsCompany
  {
    key: "ClientIsCompany",
    labelKey: "qtg.clientIsCompany.label",
    width: 100,
    isVisible: true,
    filter: {
      type: "boolean",
      value: null,
    },
    detailsListColumn: ({ t, width }) => ({
      key: "ClientIsCompany",
      label: t("qtg.clientIsCompany.label"),
      width,
      isSortable: true,
      render: ({ row }) => {
        return (
          <CenteredCell data-help="offers-client-type-icon">
            {row.ClientIsCompany ? (
              <BriefcaseRegular
                fontSize={20}
                title={t("qtg.clientType.company.label")}
              />
            ) : (
              <PersonRegular
                fontSize={20}
                title={t("qtg.clientType.person.label")}
              />
            )}
          </CenteredCell>
        );
      },
    }),
  },
  // VF ClientName
  {
    key: "ClientName",
    labelKey: "qtg.clientName.label",
    width: 220,
    isVisible: true,
    filter: {
      type: "text",
      value: "",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "ClientName",
      label: t("qtg.clientName.label"),
      width,
      isSortable: true,
    }),
  },
  // VF ClientCode
  {
    key: "ClientCode",
    labelKey: "qtg.clientCode.label",
    width: 120,
    isVisible: true,
    filter: {
      type: "text",
      value: "",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "ClientCode",
      label: t("qtg.clientCode.label"),
      width,
      isSortable: true,
    }),
  },
  // F ClientFirstName
  {
    key: "ClientFirstName",
    labelKey: "qtg.clientFirstName.label",
    width: 120,
    isVisible: false,
    filter: {
      type: "text",
      value: "",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "ClientFirstName",
      label: t("qtg.clientFirstName.label"),
      width,
      isSortable: true,
    }),
  },
  // F ClientLastName
  {
    key: "ClientLastName",
    labelKey: "qtg.clientLastName.label",
    width: 120,
    isVisible: false,
    filter: {
      type: "text",
      value: "",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "ClientLastName",
      label: t("qtg.clientLastName.label"),
      width,
      isSortable: true,
    }),
  },

  // F ClientExperience
  {
    key: "ClientExperience",
    labelKey: "qtg.clientExperience.label",
    width: 120,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "integer",
      isNegativeAllowed: false,
      includeThousandSeparators: false,
    },
    detailsListColumn: ({ t, width }) => ({
      key: "ClientExperience",
      label: t("qtg.clientExperience.label"),
      width,
      isSortable: true,
    }),
  },

  // F ClientAge
  {
    key: "ClientAge",
    labelKey: "qtg.clientAge.label",
    width: 100,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "integer",
      isNegativeAllowed: false,
      includeThousandSeparators: false,
    },
    detailsListColumn: ({ t, width }) => ({
      key: "ClientAge",
      label: t("qtg.clientAge.label"),
      width,
      isSortable: true,
    }),
  },
  // ClientAddressAddressString (no filter)
  {
    key: "ClientAddressAddressString",
    labelKey: "qtg.clientAddress.label",
    width: 200,
    isVisible: false,
    detailsListColumn: ({ t, width }) => ({
      key: "ClientAddressAddressString",
      label: t("qtg.clientAddress.label"),
      width,
      isSortable: true,
    }),
  },
  // F ClientAddressCity
  {
    key: "ClientAddressCity",
    labelKey: "qtg.clientCity.label",
    width: 120,
    isVisible: false,
    filter: {
      type: "text",
      value: "",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "ClientAddressCity",
      label: t("qtg.clientCity.label"),
      width,
      isSortable: true,
    }),
  },
  // F ClientAddressEmail
  {
    key: "ClientAddressEmail",
    labelKey: "qtg.clientEmail.label",
    width: 150,
    isVisible: false,
    filter: {
      type: "text",
      value: "",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "ClientAddressEmail",
      label: t("qtg.clientEmail.label"),
      width,
      isSortable: true,
    }),
  },
  // F ClientAddressPhone
  {
    key: "ClientAddressPhone",
    labelKey: "qtg.clientPhone.label",
    width: 150,
    isVisible: false,
    filter: {
      type: "text",
      value: "",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "ClientAddressPhone",
      label: t("qtg.clientPhone.label"),
      width,
      isSortable: true,
    }),
  },
  // VF VehicleBrand
  {
    key: "VehicleBrand",
    labelKey: "qtg.vehicleBrand.label",
    width: 80,
    isVisible: true,
    filter: {
      type: "text",
      value: "",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "VehicleBrand",
      label: t("qtg.vehicleBrand.label"),
      width,
      isSortable: true,
    }),
  },
  // VF VehicleModel
  {
    key: "VehicleModel",
    labelKey: "qtg.vehicleModel.label",
    width: 120,
    isVisible: true,
    filter: {
      type: "text",
      value: "",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "VehicleModel",
      label: t("qtg.vehicleModel.label"),
      width,
      isSortable: true,
    }),
  },
  // V VehicleLicensePlate
  {
    key: "VehicleLicensePlate",
    labelKey: "qtg.licensePlate.label",
    width: 100,
    isVisible: true,
    isFixed: false,
    detailsListColumn: ({ t, width }) => ({
      key: "VehicleLicensePlate",
      label: t("qtg.licensePlate.label"),
      width,
      isSortable: true,
    }),
  },
  // V VehicleRegistrationCertificateNumber
  {
    key: "VehicleRegistrationCertificateNumber",
    labelKey: "qtg.vehicleRegistrationCertificateNumber.label",
    width: 80,
    isVisible: true,
    isFixed: false,
    detailsListColumn: ({ t, width }) => ({
      key: "VehicleRegistrationCertificateNumber",
      label: t("qtg.vehicleRegistrationCertificateNumber.label"),
      width,
      isSortable: true,
    }),
  },
  // VehicleVin (no filter)
  {
    key: "VehicleVin",
    labelKey: "qtg.vin.label",
    width: 180,
    isVisible: false,
    detailsListColumn: ({ t, width }) => ({
      key: "VehicleVin",
      label: t("qtg.vin.label"),
      width,
      isSortable: true,
    }),
  },
  // F VehicleIsConvertible
  {
    key: "VehicleIsConvertible",
    labelKey: "qtg.vehicleIsConvertible.label",
    width: 120,
    isVisible: false,
    filter: {
      type: "boolean",
      value: null,
    },
    detailsListColumn: ({ t, width }) => ({
      key: "VehicleIsConvertible",
      label: t("qtg.vehicleIsConvertible.label"),
      width,
      isSortable: true,
      render: ({ row }) => (
        <CenteredCell>
          {row.VehicleIsConvertible ? t("greco.yes") : t("greco.no")}
        </CenteredCell>
      ),
    }),
  },
  // F VehicleTypeID
  {
    key: "VehicleTypeID",
    labelKey: "qtg.objectGroup.label",
    width: 120,
    isVisible: false,
    filter: {
      type: "taxonomy",
      taxonomyKey: "VehicleType",
      value: [],
    },
    detailsListColumn: ({ t, width }) => ({
      key: "VehicleTypeID",
      label: t("qtg.objectGroup.label"),
      width,
      isSortable: true,
      render: ({ row }) => {
        if (!row) return null;
        return <VehicleTypeCell vehicleTypeId={row.VehicleTypeID} />;
      },
    }),
  },
  // F VehicleTransportTypeCode
  {
    key: "VehicleTransportTypeCode",
    labelKey: "qtg.vehicleTransportTypeCode.label",
    width: 180,
    isVisible: false,
    filter: {
      type: "taxonomy",
      taxonomyKey: "VehicleTransportType",
      value: [],
    },
    detailsListColumn: ({ t, width }) => ({
      key: "VehicleTransportTypeCode",
      label: t("qtg.vehicleTransportTypeCode.label"),
      width,
      isSortable: true,
      render: ({ row }) => {
        if (!row) return null;
        return (
          <TransportTypeCell transportType={row.VehicleTransportTypeCode} />
        );
      },
    }),
  },
  // F VehicleValue
  {
    key: "VehicleValue",
    labelKey: "qtg.vehicleValue.label",
    width: 120,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "float",
      isNegativeAllowed: false,
      includeThousandSeparators: true,
    },
    detailsListColumn: ({ t, width }) => ({
      key: "VehicleValue",
      label: t("qtg.vehicleValue.label"),
      width,
      isSortable: true,
      transform: ({ row }) =>
        row?.VehicleValue != null ? formatCurrency(row.VehicleValue) : null,
    }),
  },
  // F VehicleCubicCapacity
  {
    key: "VehicleCubicCapacityCcm",
    labelKey: "qtg.engineCapacity.label",
    width: 100,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "integer",
      isNegativeAllowed: false,
      includeThousandSeparators: false,
    },
    detailsListColumn: ({ t, width }) => ({
      key: "VehicleCubicCapacityCcm",
      label: t("qtg.engineCapacity.label"),
      width,
      isSortable: true,
      transform: ({ row }) =>
        row?.VehicleCubicCapacityCcm != null
          ? formatNumber(row.VehicleCubicCapacityCcm, 0, 0)
          : null,
    }),
  },
  // F VehiclePowerKw
  {
    key: "VehiclePowerKw",
    labelKey: "qtg.powerKw.label",
    width: 100,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "integer",
      isNegativeAllowed: false,
      includeThousandSeparators: false,
    },
    detailsListColumn: ({ t, width }) => ({
      key: "VehiclePowerKw",
      label: t("qtg.powerKw.label"),
      width,
      isSortable: true,
    }),
  },
  // F VehicleManufactureYear
  {
    key: "VehicleManufactureYear",
    labelKey: "qtg.proposal.vehicleData.productionYear",
    width: 120,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "integer",
      isNegativeAllowed: false,
      includeThousandSeparators: false,
    },
    detailsListColumn: ({ t, width }) => ({
      key: "VehicleManufactureYear",
      label: t("qtg.proposal.vehicleData.productionYear"),
      width,
      isSortable: true,
    }),
  },
  // F InsuranceObjectUsage
  {
    key: "InsuranceObjectUsage",
    labelKey: "qtg.insuranceObjectUsage.label",
    width: 180,
    isVisible: false,
    filter: {
      type: "taxonomy",
      taxonomyKey: "VehicleUsageTypeLV",
      value: [],
    },
    detailsListColumn: ({ t, width }) => ({
      key: "InsuranceObjectUsage",
      label: t("qtg.insuranceObjectUsage.label"),
      width,
      isSortable: true,
      render: ({ row }) => {
        if (!row) return null;
        return <UsageTypeCell usageType={row.InsuranceObjectUsage} />;
      },
    }),
  },
  // F VehicleMaximumGrossWeight
  {
    key: "VehicleMaximumGrossWeight",
    labelKey: "qtg.maximumGrossWeight.label",
    width: 120,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "integer",
      isNegativeAllowed: false,
      includeThousandSeparators: false,
    },
    detailsListColumn: ({ t, width }) => ({
      key: "VehicleMaximumGrossWeight",
      label: t("qtg.maximumGrossWeight.label"),
      width,
      isSortable: true,
      transform: ({ row }) =>
        row?.VehicleMaximumGrossWeight != null
          ? formatNumber(row.VehicleMaximumGrossWeight, 0, 0)
          : null,
    }),
  },
  // F VehicleSeats
  {
    key: "VehicleSeats",
    labelKey: "qtg.seats.label",
    width: 80,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "integer",
      isNegativeAllowed: false,
      includeThousandSeparators: false,
    },
    detailsListColumn: ({ t, width }) => ({
      key: "VehicleSeats",
      label: t("qtg.seats.label"),
      width,
      isSortable: true,
    }),
  },
  // F VehicleIsKeyless
  {
    key: "VehicleIsKeyless",
    labelKey: "qtg.vehicleIsKeyless.label",
    width: 120,
    isVisible: false,
    filter: {
      type: "boolean",
      value: null,
    },
    detailsListColumn: ({ t, width }) => ({
      key: "VehicleIsKeyless",
      label: t("qtg.vehicleIsKeyless.label"),
      width,
      isSortable: true,
      render: ({ row }) => (
        <CenteredCell>
          {row.VehicleIsKeyless ? t("greco.yes") : t("greco.no")}
        </CenteredCell>
      ),
    }),
  },
  // F VehiclePowerTypeCode
  {
    key: "VehiclePowerTypeCode",
    labelKey: "qtg.fuelType.label",
    width: 150,
    isVisible: false,
    filter: {
      type: "taxonomy",
      taxonomyKey: "VehiclePowerType",
      value: [],
    },
    detailsListColumn: ({ t, width }) => ({
      key: "VehiclePowerTypeCode",
      label: t("qtg.fuelType.label"),
      width,
      isSortable: true,
      render: ({ row }) => {
        if (!row) return null;
        return <PowerTypeCell powerType={row.VehiclePowerTypeCode} />;
      },
    }),
  },
  // F OwnerCode
  {
    key: "OwnerCode",
    labelKey: "qtg.ownerCode.label",
    width: 120,
    isVisible: false,
    filter: {
      type: "text",
      value: "",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "OwnerCode",
      label: t("qtg.ownerCode.label"),
      width,
      isSortable: true,
    }),
  },
  // F OwnerName
  {
    key: "OwnerName",
    labelKey: "qtg.ownerName.label",
    width: 180,
    isVisible: false,
    filter: {
      type: "text",
      value: "",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "OwnerName",
      label: t("qtg.ownerName.label"),
      width,
      isSortable: true,
    }),
  },
  // F OwnerAddressEmail
  {
    key: "OwnerAddressEmail",
    labelKey: "qtg.ownerEmail.label",
    width: 150,
    isVisible: false,
    filter: {
      type: "text",
      value: "",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "OwnerAddressEmail",
      label: t("qtg.ownerEmail.label"),
      width,
      isSortable: true,
    }),
  },
  // F OwnerAddressPhone
  {
    key: "OwnerAddressPhone",
    labelKey: "qtg.ownerPhone.label",
    width: 150,
    isVisible: false,
    filter: {
      type: "text",
      value: "",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "OwnerAddressPhone",
      label: t("qtg.ownerPhone.label"),
      width,
      isSortable: true,
    }),
  },
  // VF InsuranceStartDate
  {
    key: "InsuranceStartDate",
    labelKey: "qtg.policyStartDate.label",
    width: 80,
    isVisible: true,
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isAfter",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "InsuranceStartDate",
      label: t("qtg.policyStartDate.label"),
      width,
      isSortable: true,
      transform: ({ row }) =>
        row?.InsuranceStartDate
          ? formatDateString(row?.InsuranceStartDate)
          : null,
    }),
  },
  // VF OfferRequestedAt
  {
    key: "OfferRequestedAt",
    labelKey: "qtg.offerRequestedAt.label",
    width: 80,
    isVisible: true,
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isAfter",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "OfferRequestedAt",
      label: t("qtg.offerRequestedAt.label"),
      width,
      isSortable: true,
      transform: ({ row }) =>
        row?.OfferRequestedAt ? formatDateString(row?.OfferRequestedAt) : null,
    }),
  },
  // VF OfferRequestedByUsername
  {
    key: "OfferRequestedByUsername",
    labelKey: "qtg.offerRequestedByUsername.label",
    width: 150,
    isVisible: true,
    filter: {
      type: "text",
      value: "",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "OfferRequestedByUsername",
      label: t("qtg.offerRequestedByUsername.label"),
      width,
      isSortable: true,
    }),
  },
  // F PolicyMtplInsurer
  // {
  //   key: "PolicyMtplInsurer",
  //   labelKey: "qtg.policyMtplInsurer.label",
  //   width: 150,
  //   isVisible: false,
  //   filter: {
  //     type: "text",
  //     value: "",
  //   },
  //   detailsListColumn: ({ t, width }) => ({
  //     key: "PolicyMtplInsurer",
  //     label: t("qtg.policyMtplInsurer.label"),
  //     width,
  //     isSortable: true,
  //   }),
  // },
  // // F PolicyMtplStartDate
  // {
  //   key: "PolicyMtplStartDate",
  //   labelKey: "qtg.policyMtplStartDate.label",
  //   width: 150,
  //   isVisible: false,
  //   filter: {
  //     type: "date",
  //     date1: null,
  //     date2: null,
  //     operator: "isAfter",
  //   },
  //   detailsListColumn: ({ t, width }) => ({
  //     key: "PolicyMtplStartDate",
  //     label: t("qtg.policyMtplStartDate.label"),
  //     width,
  //     isSortable: true,
  //     transform: ({ row }) =>
  //       row?.PolicyMtplStartDate
  //         ? formatDateString(row?.PolicyMtplStartDate)
  //         : null,
  //   }),
  // },
  // // F PolicyMtplEndDate
  // {
  //   key: "PolicyMtplEndDate",
  //   labelKey: "qtg.policyMtplEndDate.label",
  //   width: 150,
  //   isVisible: false,
  //   filter: {
  //     type: "date",
  //     date1: null,
  //     date2: null,
  //     operator: "isAfter",
  //   },
  //   detailsListColumn: ({ t, width }) => ({
  //     key: "PolicyMtplEndDate",
  //     label: t("qtg.policyMtplEndDate.label"),
  //     width,
  //     isSortable: true,
  //     transform: ({ row }) =>
  //       row?.PolicyMtplEndDate
  //         ? formatDateString(row?.PolicyMtplEndDate)
  //         : null,
  //   }),
  // },
  // // PolicyMtplPolicyNumber (no filter)
  // {
  //   key: "PolicyMtplPolicyNumber",
  //   labelKey: "qtg.policyMtplPolicyNumber.label",
  //   width: 150,
  //   isVisible: false,
  //   detailsListColumn: ({ t, width }) => ({
  //     key: "PolicyMtplPolicyNumber",
  //     label: t("qtg.policyMtplPolicyNumber.label"),
  //     width,
  //     isSortable: true,
  //   }),
  // },
  // // F PolicyMtplPolicyPremiumUnderwritten
  // {
  //   key: "PolicyMtplPolicyPremiumUnderwritten",
  //   labelKey: "qtg.policyMtplPremium.label",
  //   width: 150,
  //   isVisible: false,
  //   filter: {
  //     type: "number",
  //     number1: null,
  //     number2: null,
  //     operator: "gt",
  //     format: "float",
  //     isNegativeAllowed: false,
  //     includeThousandSeparators: true,
  //   },
  //   detailsListColumn: ({ t, width }) => ({
  //     key: "PolicyMtplPolicyPremiumUnderwritten",
  //     label: t("qtg.policyMtplPremium.label"),
  //     width,
  //     isSortable: true,
  //   }),
  // },
  // // F PolicyMtplIssuingPeriod
  // {
  //   key: "PolicyMtplIssuingPeriod",
  //   labelKey: "qtg.policyMtplIssuingPeriod.label",
  //   width: 150,
  //   isVisible: false,
  //   filter: {
  //     type: "number",
  //     number1: null,
  //     number2: null,
  //     operator: "gt",
  //     format: "integer",
  //     isNegativeAllowed: false,
  //     includeThousandSeparators: false,
  //   },
  //   detailsListColumn: ({ t, width }) => ({
  //     key: "PolicyMtplIssuingPeriod",
  //     label: t("qtg.policyMtplIssuingPeriod.label"),
  //     width,
  //     isSortable: true,
  //   }),
  // },
  // // F PolicyMtplIssuingPayments
  // {
  //   key: "PolicyMtplIssuingPayments",
  //   labelKey: "qtg.policyMtplIssuingPayments.label",
  //   width: 150,
  //   isVisible: false,
  //   filter: {
  //     type: "number",
  //     number1: null,
  //     number2: null,
  //     operator: "gt",
  //     format: "integer",
  //     isNegativeAllowed: false,
  //     includeThousandSeparators: false,
  //   },
  //   detailsListColumn: ({ t, width }) => ({
  //     key: "PolicyMtplIssuingPayments",
  //     label: t("qtg.policyMtplIssuingPayments.label"),
  //     width,
  //     isSortable: true,
  //   }),
  // },
  // // F PolicyMtplIssuedAt
  // {
  //   key: "PolicyMtplPolicyIssuedAt",
  //   labelKey: "qtg.policyMtplIssuedAt.label",
  //   width: 150,
  //   isVisible: false,
  //   filter: {
  //     type: "date",
  //     date1: null,
  //     date2: null,
  //     operator: "isAfter",
  //   },
  //   detailsListColumn: ({ t, width }) => ({
  //     key: "PolicyMtplPolicyIssuedAt",
  //     label: t("qtg.policyMtplIssuedAt.label"),
  //     width,
  //     isSortable: true,
  //     transform: ({ row }) =>
  //       row?.PolicyMtplPolicyIssuedAt
  //         ? formatDateString(row?.PolicyMtplPolicyIssuedAt)
  //         : null,
  //   }),
  // },
  // // F PolicyMtplIssuedByUsername
  // {
  //   key: "PolicyMtplPolicyIssuedByUsername",
  //   labelKey: "qtg.policyMtplIssuedByUsername.label",
  //   width: 150,
  //   isVisible: false,
  //   filter: {
  //     type: "text",
  //     value: "",
  //   },
  //   detailsListColumn: ({ t, width }) => ({
  //     key: "PolicyMtplPolicyIssuedByUsername",
  //     label: t("qtg.policyMtplIssuedByUsername.label"),
  //     width,
  //     isSortable: true,
  //   }),
  // },
  // // F PolicyCascoInsurer
  // {
  //   key: "PolicyCascoInsurer",
  //   labelKey: "qtg.policyCascoInsurer.label",
  //   width: 150,
  //   isVisible: false,
  //   filter: {
  //     type: "text",
  //     value: "",
  //   },
  //   detailsListColumn: ({ t, width }) => ({
  //     key: "PolicyCascoInsurer",
  //     label: t("qtg.policyCascoInsurer.label"),
  //     width,
  //     isSortable: true,
  //   }),
  // },
  // // F PolicyCascoStartDate
  // {
  //   key: "PolicyCascoStartDate",
  //   labelKey: "qtg.policyCascoStartDate.label",
  //   width: 150,
  //   isVisible: false,
  //   filter: {
  //     type: "date",
  //     date1: null,
  //     date2: null,
  //     operator: "isAfter",
  //   },
  //   detailsListColumn: ({ t, width }) => ({
  //     key: "PolicyCascoStartDate",
  //     label: t("qtg.policyCascoStartDate.label"),
  //     width,
  //     isSortable: true,
  //     transform: ({ row }) =>
  //       row?.PolicyCascoStartDate
  //         ? formatDateString(row?.PolicyCascoStartDate)
  //         : null,
  //   }),
  // },
  // // F PolicyCascoEndDate
  // {
  //   key: "PolicyCascoEndDate",
  //   labelKey: "qtg.policyCascoEndDate.label",
  //   width: 150,
  //   isVisible: false,
  //   filter: {
  //     type: "date",
  //     date1: null,
  //     date2: null,
  //     operator: "isAfter",
  //   },
  //   detailsListColumn: ({ t, width }) => ({
  //     key: "PolicyCascoEndDate",
  //     label: t("qtg.policyCascoEndDate.label"),
  //     width,
  //     isSortable: true,
  //     transform: ({ row }) =>
  //       row?.PolicyCascoEndDate
  //         ? formatDateString(row?.PolicyCascoEndDate)
  //         : null,
  //   }),
  // },
  // // PolicyCascoPolicyNumber (no filter)
  // {
  //   key: "PolicyCascoPolicyNumber",
  //   labelKey: "qtg.policyCascoPolicyNumber.label",
  //   width: 150,
  //   isVisible: false,
  //   detailsListColumn: ({ t, width }) => ({
  //     key: "PolicyCascoPolicyNumber",
  //     label: t("qtg.policyCascoPolicyNumber.label"),
  //     width,
  //     isSortable: true,
  //   }),
  // },
  // // F PolicyCascoPolicyPremiumUnderwritten
  // {
  //   key: "PolicyCascoPolicyPremiumUnderwritten",
  //   labelKey: "qtg.policyCascoPremium.label",
  //   width: 150,
  //   isVisible: false,
  //   filter: {
  //     type: "number",
  //     number1: null,
  //     number2: null,
  //     operator: "gt",
  //     format: "float",
  //     isNegativeAllowed: false,
  //     includeThousandSeparators: true,
  //   },
  //   detailsListColumn: ({ t, width }) => ({
  //     key: "PolicyCascoPolicyPremiumUnderwritten",
  //     label: t("qtg.policyCascoPremium.label"),
  //     width,
  //     isSortable: true,
  //   }),
  // },
  // // F PolicyCascoIssuingPeriod
  // {
  //   key: "PolicyCascoIssuingPeriod",
  //   labelKey: "qtg.policyCascoIssuingPeriod.label",
  //   width: 150,
  //   isVisible: false,
  //   filter: {
  //     type: "number",
  //     number1: null,
  //     number2: null,
  //     operator: "gt",
  //     format: "integer",
  //     isNegativeAllowed: false,
  //     includeThousandSeparators: false,
  //   },
  //   detailsListColumn: ({ t, width }) => ({
  //     key: "PolicyCascoIssuingPeriod",
  //     label: t("qtg.policyCascoIssuingPeriod.label"),
  //     width,
  //     isSortable: true,
  //   }),
  // },
  // // F PolicyCascoIssuingPayments
  // {
  //   key: "PolicyCascoIssuingPayments",
  //   labelKey: "qtg.policyCascoIssuingPayments.label",
  //   width: 150,
  //   isVisible: false,
  //   filter: {
  //     type: "number",
  //     number1: null,
  //     number2: null,
  //     operator: "gt",
  //     format: "integer",
  //     isNegativeAllowed: false,
  //     includeThousandSeparators: false,
  //   },
  //   detailsListColumn: ({ t, width }) => ({
  //     key: "PolicyCascoIssuingPayments",
  //     label: t("qtg.policyCascoIssuingPayments.label"),
  //     width,
  //     isSortable: true,
  //   }),
  // },
  // // F PolicyCascoIssuedAt
  // {
  //   key: "PolicyCascoPolicyIssuedAt",
  //   labelKey: "qtg.policyCascoIssuedAt.label",
  //   width: 150,
  //   isVisible: false,
  //   filter: {
  //     type: "date",
  //     date1: null,
  //     date2: null,
  //     operator: "isAfter",
  //   },
  //   detailsListColumn: ({ t, width }) => ({
  //     key: "PolicyCascoPolicyIssuedAt",
  //     label: t("qtg.policyCascoIssuedAt.label"),
  //     width,
  //     isSortable: true,
  //     transform: ({ row }) =>
  //       row?.PolicyCascoPolicyIssuedAt
  //         ? formatDateString(row?.PolicyCascoPolicyIssuedAt)
  //         : null,
  //   }),
  // },
  // // F PolicyCascoIssuedByUsername
  // {
  //   key: "PolicyCascoPolicyIssuedByUsername",
  //   labelKey: "qtg.policyCascoIssuedByUsername.label",
  //   width: 150,
  //   isVisible: false,
  //   filter: {
  //     type: "text",
  //     value: "",
  //   },
  //   detailsListColumn: ({ t, width }) => ({
  //     key: "PolicyCascoPolicyIssuedByUsername",
  //     label: t("qtg.policyCascoIssuedByUsername.label"),
  //     width,
  //     isSortable: true,
  //   }),
  // },
  // F InvoiceMtplInvoiceIssuedAt
  {
    key: "InvoiceMtplInvoiceIssuedAt",
    labelKey: "qtg.invoiceMtplIssuedAt.label",
    width: 150,
    isVisible: false,
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isAfter",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "InvoiceMtplInvoiceIssuedAt",
      label: t("qtg.invoiceMtplIssuedAt.label"),
      width,
      isSortable: true,
      transform: ({ row }) =>
        row?.InvoiceMtplInvoiceIssuedAt
          ? formatDateString(row?.InvoiceMtplInvoiceIssuedAt)
          : null,
    }),
  },
  // F InvoiceMtplInvoiceIssuedByUsername
  {
    key: "InvoiceMtplInvoiceIssuedByUsername",
    labelKey: "qtg.invoiceMtplIssuedByUsername.label",
    width: 150,
    isVisible: false,
    filter: {
      type: "text",
      value: "",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "InvoiceMtplInvoiceIssuedByUsername",
      label: t("qtg.invoiceMtplIssuedByUsername.label"),
      width,
      isSortable: true,
    }),
  },
  // F InvoiceMtplIsPaid
  {
    key: "InvoiceMtplIsPaid",
    labelKey: "qtg.invoiceMtplIsPaid.label",
    width: 120,
    isVisible: false,
    filter: {
      type: "boolean",
      value: null,
    },
    detailsListColumn: ({ t, width }) => ({
      key: "InvoiceMtplIsPaid",
      label: t("qtg.invoiceMtplIsPaid.label"),
      width,
      isSortable: true,
      render: ({ row }) => (
        <CenteredCell>
          {row.InvoiceMtplIsPaid === null
            ? ""
            : row.InvoiceMtplIsPaid
              ? t("greco.yes")
              : t("greco.no")}
        </CenteredCell>
      ),
    }),
  },
  // F InvoiceMtplPaidAt
  {
    key: "InvoiceMtplPaidAt",
    labelKey: "qtg.invoiceMtplPaidAt.label",
    width: 150,
    isVisible: false,
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isAfter",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "InvoiceMtplPaidAt",
      label: t("qtg.invoiceMtplPaidAt.label"),
      width,
      isSortable: true,
      transform: ({ row }) =>
        row?.InvoiceMtplPaidAt
          ? formatDateString(row?.InvoiceMtplPaidAt)
          : null,
    }),
  },
  // F InvoiceMtplInsurer
  {
    key: "InvoiceMtplInsurer",
    labelKey: "qtg.invoiceMtplInsurer.label",
    width: 150,
    isVisible: false,
    filter: {
      type: "text",
      value: "",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "InvoiceMtplInsurer",
      label: t("qtg.invoiceMtplInsurer.label"),
      width,
      isSortable: true,
    }),
  },
  // InvoiceMtplInvoiceNumber
  {
    key: "InvoiceMtplInvoiceNumber",
    labelKey: "qtg.invoiceMtplInvoiceNumber.label",
    width: 150,
    isVisible: false,
    detailsListColumn: ({ t, width }) => ({
      key: "InvoiceMtplInvoiceNumber",
      label: t("qtg.invoiceMtplInvoiceNumber.label"),
      width,
      isSortable: true,
    }),
  },
  // F InvoiceMtplDueDate
  {
    key: "InvoiceMtplDueDate",
    labelKey: "qtg.invoiceMtplDueDate.label",
    width: 150,
    isVisible: false,
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isAfter",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "InvoiceMtplDueDate",
      label: t("qtg.invoiceMtplDueDate.label"),
      width,
      isSortable: true,
      transform: ({ row }) =>
        row?.InvoiceMtplDueDate
          ? formatDateString(row?.InvoiceMtplDueDate)
          : null,
    }),
  },
  // InvoiceMtplPremium
  {
    key: "InvoiceMtplPremium",
    labelKey: "qtg.invoiceMtplPremium.label",
    width: 120,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "float",
      isNegativeAllowed: false,
    },
    detailsListColumn: ({ t, width }) => ({
      key: "InvoiceMtplPremium",
      label: t("qtg.invoiceMtplPremium.label"),
      width,
      isSortable: true,
      render: ({ row }) => (
        <RightAlignedCell>
          {row?.InvoiceMtplPremium != null
            ? formatCurrency(row.InvoiceMtplPremium)
            : null}
        </RightAlignedCell>
      ),
    }),
  },
  // InvoiceMtplDiscountPercent
  {
    key: "InvoiceMtplDiscountPercent",
    labelKey: "qtg.invoiceMtplDiscountPercent.label",
    width: 120,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "float",
      isNegativeAllowed: false,
    },
    detailsListColumn: ({ t, width }) => ({
      key: "InvoiceMtplDiscountPercent",
      label: t("qtg.invoiceMtplDiscountPercent.label"),
      width,
      isSortable: true,
      render: ({ row }) => (
        <RightAlignedCell>
          {row?.InvoiceMtplDiscountPercent != null
            ? formatPercent(row.InvoiceMtplDiscountPercent)
            : null}
        </RightAlignedCell>
      ),
    }),
  },
  // InvoiceMtplDiscount
  {
    key: "InvoiceMtplDiscount",
    labelKey: "qtg.invoiceMtplDiscount.label",
    width: 120,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "float",
      isNegativeAllowed: false,
    },
    detailsListColumn: ({ t, width }) => ({
      key: "InvoiceMtplDiscount",
      label: t("qtg.invoiceMtplDiscount.label"),
      width,
      isSortable: true,
      render: ({ row }) => (
        <RightAlignedCell>
          {row?.InvoiceMtplDiscount != null
            ? formatCurrency(row.InvoiceMtplDiscount)
            : null}
        </RightAlignedCell>
      ),
    }),
  },
  // InvoiceMtplPeriod
  {
    key: "InvoiceMtplPeriod",
    labelKey: "qtg.invoiceMtplPeriod.label",
    width: 110,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "integer",
      isNegativeAllowed: false,
      includeThousandSeparators: false,
    },
    detailsListColumn: ({ t, width }) => ({
      key: "InvoiceMtplPeriod",
      label: t("qtg.invoiceMtplPeriod.label"),
      width,
      isSortable: true,
      render: ({ row }) => (
        <RightAlignedCell>
          {row?.InvoiceMtplPeriod != null ? formatNumber(row.InvoiceMtplPeriod, 0, 0) : null}
        </RightAlignedCell>
      ),
    }),
  },
  // InvoiceMtplPayments
  {
    key: "InvoiceMtplPayments",
    labelKey: "qtg.invoiceMtplPayments.label",
    width: 110,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "integer",
      isNegativeAllowed: false,
      includeThousandSeparators: false,
    },
    detailsListColumn: ({ t, width }) => ({
      key: "InvoiceMtplPayments",
      label: t("qtg.invoiceMtplPayments.label"),
      width,
      isSortable: true,
      render: ({ row }) => (
        <RightAlignedCell>
          {row?.InvoiceMtplPayments != null ? formatNumber(row.InvoiceMtplPayments, 0, 0) : null}
        </RightAlignedCell>
      ),
    }),
  },
  // F payer of the MTPL invoice (Invoice.Mtpl.Request.Payer)
  ...invoicePayerColumns("InvoiceMtplPayer", "qtg.invoiceMtplPayer"),
  // F InvoiceCascoInvoiceIssuedAt
  {
    key: "InvoiceCascoInvoiceIssuedAt",
    labelKey: "qtg.invoiceCascoIssuedAt.label",
    width: 150,
    isVisible: false,
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isAfter",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "InvoiceCascoInvoiceIssuedAt",
      label: t("qtg.invoiceCascoIssuedAt.label"),
      width,
      isSortable: true,
      transform: ({ row }) =>
        row?.InvoiceCascoInvoiceIssuedAt
          ? formatDateString(row?.InvoiceCascoInvoiceIssuedAt)
          : null,
    }),
  },
  // F InvoiceCascoInvoiceIssuedByUsername
  {
    key: "InvoiceCascoInvoiceIssuedByUsername",
    labelKey: "qtg.invoiceCascoIssuedByUsername.label",
    width: 150,
    isVisible: false,
    filter: {
      type: "text",
      value: "",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "InvoiceCascoInvoiceIssuedByUsername",
      label: t("qtg.invoiceCascoIssuedByUsername.label"),
      width,
      isSortable: true,
    }),
  },
  // F InvoiceCascoIsPaid
  {
    key: "InvoiceCascoIsPaid",
    labelKey: "qtg.invoiceCascoIsPaid.label",
    width: 120,
    isVisible: false,
    filter: {
      type: "boolean",
      value: null,
    },
    detailsListColumn: ({ t, width }) => ({
      key: "InvoiceCascoIsPaid",
      label: t("qtg.invoiceCascoIsPaid.label"),
      width,
      isSortable: true,
      render: ({ row }) => (
        <CenteredCell>
          {row.InvoiceCascoIsPaid === null
            ? ""
            : row.InvoiceCascoIsPaid
              ? t("greco.yes")
              : t("greco.no")}
        </CenteredCell>
      ),
    }),
  },
  // F InvoiceCascoPaidAt
  {
    key: "InvoiceCascoPaidAt",
    labelKey: "qtg.invoiceCascoPaidAt.label",
    width: 150,
    isVisible: false,
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isAfter",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "InvoiceCascoPaidAt",
      label: t("qtg.invoiceCascoPaidAt.label"),
      width,
      isSortable: true,
      transform: ({ row }) =>
        row?.InvoiceCascoPaidAt
          ? formatDateString(row?.InvoiceCascoPaidAt)
          : null,
    }),
  },
  // F InvoiceCascoInsurer
  {
    key: "InvoiceCascoInsurer",
    labelKey: "qtg.invoiceCascoInsurer.label",
    width: 150,
    isVisible: false,
    filter: {
      type: "text",
      value: "",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "InvoiceCascoInsurer",
      label: t("qtg.invoiceCascoInsurer.label"),
      width,
      isSortable: true,
    }),
  },
  // InvoiceCascoInvoiceNumber
  {
    key: "InvoiceCascoInvoiceNumber",
    labelKey: "qtg.invoiceCascoInvoiceNumber.label",
    width: 150,
    isVisible: false,
    detailsListColumn: ({ t, width }) => ({
      key: "InvoiceCascoInvoiceNumber",
      label: t("qtg.invoiceCascoInvoiceNumber.label"),
      width,
      isSortable: true,
    }),
  },
  // F InvoiceCascoDueDate
  {
    key: "InvoiceCascoDueDate",
    labelKey: "qtg.invoiceCascoDueDate.label",
    width: 150,
    isVisible: false,
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isAfter",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "InvoiceCascoDueDate",
      label: t("qtg.invoiceCascoDueDate.label"),
      width,
      isSortable: true,
      transform: ({ row }) =>
        row?.InvoiceCascoDueDate
          ? formatDateString(row?.InvoiceCascoDueDate)
          : null,
    }),
  },
  // InvoiceCascoPremium
  {
    key: "InvoiceCascoPremium",
    labelKey: "qtg.invoiceCascoPremium.label",
    width: 120,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "float",
      isNegativeAllowed: false,
    },
    detailsListColumn: ({ t, width }) => ({
      key: "InvoiceCascoPremium",
      label: t("qtg.invoiceCascoPremium.label"),
      width,
      isSortable: true,
      render: ({ row }) => (
        <RightAlignedCell>
          {row?.InvoiceCascoPremium != null
            ? formatCurrency(row.InvoiceCascoPremium)
            : null}
        </RightAlignedCell>
      ),
    }),
  },
  // InvoiceCascoDiscountPercent
  {
    key: "InvoiceCascoDiscountPercent",
    labelKey: "qtg.invoiceCascoDiscountPercent.label",
    width: 120,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "float",
      isNegativeAllowed: false,
    },
    detailsListColumn: ({ t, width }) => ({
      key: "InvoiceCascoDiscountPercent",
      label: t("qtg.invoiceCascoDiscountPercent.label"),
      width,
      isSortable: true,
      render: ({ row }) => (
        <RightAlignedCell>
          {row?.InvoiceCascoDiscountPercent != null
            ? formatPercent(row.InvoiceCascoDiscountPercent)
            : null}
        </RightAlignedCell>
      ),
    }),
  },
  // InvoiceCascoDiscount
  {
    key: "InvoiceCascoDiscount",
    labelKey: "qtg.invoiceCascoDiscount.label",
    width: 120,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "float",
      isNegativeAllowed: false,
    },
    detailsListColumn: ({ t, width }) => ({
      key: "InvoiceCascoDiscount",
      label: t("qtg.invoiceCascoDiscount.label"),
      width,
      isSortable: true,
      render: ({ row }) => (
        <RightAlignedCell>
          {row?.InvoiceCascoDiscount != null
            ? formatCurrency(row.InvoiceCascoDiscount)
            : null}
        </RightAlignedCell>
      ),
    }),
  },
  // InvoiceCascoPeriod
  {
    key: "InvoiceCascoPeriod",
    labelKey: "qtg.invoiceCascoPeriod.label",
    width: 110,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "integer",
      isNegativeAllowed: false,
      includeThousandSeparators: false,
    },
    detailsListColumn: ({ t, width }) => ({
      key: "InvoiceCascoPeriod",
      label: t("qtg.invoiceCascoPeriod.label"),
      width,
      isSortable: true,
      render: ({ row }) => (
        <RightAlignedCell>
          {row?.InvoiceCascoPeriod != null ? formatNumber(row.InvoiceCascoPeriod, 0, 0) : null}
        </RightAlignedCell>
      ),
    }),
  },
  // InvoiceCascoPayments
  {
    key: "InvoiceCascoPayments",
    labelKey: "qtg.invoiceCascoPayments.label",
    width: 110,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "integer",
      isNegativeAllowed: false,
      includeThousandSeparators: false,
    },
    detailsListColumn: ({ t, width }) => ({
      key: "InvoiceCascoPayments",
      label: t("qtg.invoiceCascoPayments.label"),
      width,
      isSortable: true,
      render: ({ row }) => (
        <RightAlignedCell>
          {row?.InvoiceCascoPayments != null ? formatNumber(row.InvoiceCascoPayments, 0, 0) : null}
        </RightAlignedCell>
      ),
    }),
  },
  // F payer of the CASCO invoice (Invoice.Casco.Request.Payer)
  ...invoicePayerColumns("InvoiceCascoPayer", "qtg.invoiceCascoPayer"),
  // F GroupInvoiceNumber
  {
    key: "GroupInvoiceNumber",
    labelKey: "qtg.groupInvoiceNumber.label",
    width: 150,
    isVisible: false,
    filter: {
      type: "text",
      value: "",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "GroupInvoiceNumber",
      label: t("qtg.groupInvoiceNumber.label"),
      width,
      isSortable: true,
    }),
  },
  // F GroupInvoiceIssuedAt
  {
    key: "GroupInvoiceIssuedAt",
    labelKey: "qtg.groupInvoiceIssuedAt.label",
    width: 150,
    isVisible: false,
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isAfter",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "GroupInvoiceIssuedAt",
      label: t("qtg.groupInvoiceIssuedAt.label"),
      width,
      isSortable: true,
      transform: ({ row }) =>
        row?.GroupInvoiceIssuedAt
          ? formatDateString(row?.GroupInvoiceIssuedAt)
          : null,
    }),
  },
  // F GroupInvoiceIssuedByUsername
  {
    key: "GroupInvoiceIssuedByUsername",
    labelKey: "qtg.groupInvoiceIssuedByUsername.label",
    width: 150,
    isVisible: false,
    filter: {
      type: "text",
      value: "",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "GroupInvoiceIssuedByUsername",
      label: t("qtg.groupInvoiceIssuedByUsername.label"),
      width,
      isSortable: true,
    }),
  },
  // F GroupInvoiceIsPaid
  {
    key: "GroupInvoiceIsPaid",
    labelKey: "qtg.groupInvoiceIsPaid.label",
    width: 120,
    isVisible: false,
    filter: {
      type: "boolean",
      value: null,
    },
    detailsListColumn: ({ t, width }) => ({
      key: "GroupInvoiceIsPaid",
      label: t("qtg.groupInvoiceIsPaid.label"),
      width,
      isSortable: true,
      render: ({ row }) => (
        <CenteredCell>
          {row.GroupInvoiceIsPaid === null
            ? ""
            : row.GroupInvoiceIsPaid
              ? t("greco.yes")
              : t("greco.no")}
        </CenteredCell>
      ),
    }),
  },
  // F GroupInvoicePaidAt
  {
    key: "GroupInvoicePaidAt",
    labelKey: "qtg.groupInvoicePaidAt.label",
    width: 150,
    isVisible: false,
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isAfter",
    },
    detailsListColumn: ({ t, width }) => ({
      key: "GroupInvoicePaidAt",
      label: t("qtg.groupInvoicePaidAt.label"),
      width,
      isSortable: true,
      transform: ({ row }) =>
        row?.GroupInvoicePaidAt
          ? formatDateString(row?.GroupInvoicePaidAt)
          : null,
    }),
  },
  // GroupInvoicePremium
  {
    key: "GroupInvoicePremium",
    labelKey: "qtg.groupInvoicePremium.label",
    width: 120,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "float",
      isNegativeAllowed: false,
    },
    detailsListColumn: ({ t, width }) => ({
      key: "GroupInvoicePremium",
      label: t("qtg.groupInvoicePremium.label"),
      width,
      isSortable: true,
      render: ({ row }) => (
        <RightAlignedCell>
          {row?.GroupInvoicePremium != null
            ? formatCurrency(row.GroupInvoicePremium)
            : null}
        </RightAlignedCell>
      ),
    }),
  },
  // GroupInvoiceDiscount
  {
    key: "GroupInvoiceDiscount",
    labelKey: "qtg.groupInvoiceDiscount.label",
    width: 120,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "float",
      isNegativeAllowed: false,
    },
    detailsListColumn: ({ t, width }) => ({
      key: "GroupInvoiceDiscount",
      label: t("qtg.groupInvoiceDiscount.label"),
      width,
      isSortable: true,
      render: ({ row }) => (
        <RightAlignedCell>
          {row?.GroupInvoiceDiscount != null
            ? formatCurrency(row.GroupInvoiceDiscount)
            : null}
        </RightAlignedCell>
      ),
    }),
  },
  // F payer of the group invoice (Invoice.GroupInvoice.Request.Payer)
  ...invoicePayerColumns("GroupInvoicePayer", "qtg.groupInvoicePayer"),
];
